import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Navbar } from '@/components/Navbar';
import { BrandWatermark } from '@/components/BrandWatermark';
import { useAuth } from '@/context/AuthContext';
import { UserBadgesLoader } from '@/components/UserBadges';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Calendar, AlertTriangle, Clock, Plus, Trash2, Zap, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const impactColors = {
  high: 'text-red-400 bg-red-500/20 border-red-500/30',
  medium: 'text-orange-400 bg-orange-500/20 border-orange-500/30',
  low: 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30',
};

const impactLabels = { high: 'ALTO', medium: 'MEDIO', low: 'BAJO' };

export default function EconomicNews() {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', impact: 'high', event_time: '' });

  useEffect(() => { fetchAlerts(); }, []);

  const fetchAlerts = async () => {
    try {
      const res = await axios.get(`${API}/alerts`);
      setAlerts(res.data);
    } catch {}
  };

  const createAlert = async () => {
    if (!form.title || !form.event_time) {
      toast.error('Título y hora del evento son requeridos');
      return;
    }
    try {
      // Append GMT-5 timezone to the event_time so it's stored correctly
      const eventTimeWithTz = form.event_time + '-05:00';
      await axios.post(`${API}/alerts`, { ...form, event_time: eventTimeWithTz });
      setForm({ title: '', description: '', impact: 'high', event_time: '' });
      setShowForm(false);
      fetchAlerts();
      toast.success('Alerta creada exitosamente');
    } catch (err) {
      console.error('Alert error:', err.response?.data);
      toast.error(err.response?.data?.detail || 'Error al crear alerta');
    }
  };

  const deleteAlert = async (id) => {
    try {
      await axios.delete(`${API}/alerts/${id}`);
      fetchAlerts();
    } catch {}
  };

  const getTimeStatus = (eventTime) => {
    const now = new Date();
    const event = new Date(eventTime);
    const diffMs = event - now;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 0) return { text: 'Pasado', urgent: false };
    if (diffMin <= 15) return { text: `En ${diffMin} min`, urgent: true };
    if (diffMin <= 60) return { text: `En ${diffMin} min`, urgent: false };
    const hours = Math.floor(diffMin / 60);
    return { text: `En ${hours}h ${diffMin % 60}m`, urgent: false };
  };

  return (
    <div className="min-h-screen bg-[#020402]">
      <Navbar />
      <BrandWatermark />

      <div className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2 text-emerald-50" style={{ fontFamily: 'Rajdhani, sans-serif' }} data-testid="news-title">
                <span className="flex items-center space-x-3">
                  <span>NOTICIAS Y CALENDARIO</span>
                  <UserBadgesLoader size="md" />
                </span>
              </h1>
              <p className="text-emerald-100/70">Fundamentales, alertas y calendario económico</p>
            </div>
            <img src="/brand_logo.png" alt="" className="h-12 opacity-40 hidden md:block" />
          </div>

          {/* Alerts Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5 text-red-400" />
                <h2 className="text-xl font-bold text-emerald-50" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                  ALERTAS DE VOLATILIDAD
                </h2>
              </div>
              {user?.role === 'admin' && (
                <Button size="sm" onClick={() => setShowForm(!showForm)} className="bg-emerald-500 text-black hover:bg-emerald-400" data-testid="add-alert-btn">
                  <Plus className="h-4 w-4 mr-1" /> Alerta
                </Button>
              )}
            </div>

            {/* Admin Alert Form */}
            {showForm && user?.role === 'admin' && (
              <div className="glass-panel rounded-lg p-4 mb-4 space-y-3" data-testid="alert-form">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-emerald-100/70 text-xs">Título</Label>
                    <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Habla Powell - Fed" className="bg-black/50 border-emerald-500/30" data-testid="alert-title-input" />
                  </div>
                  <div>
                    <Label className="text-emerald-100/70 text-xs">Hora del evento</Label>
                    <Input type="datetime-local" value={form.event_time} onChange={(e) => setForm({...form, event_time: e.target.value})} className="bg-black/50 border-emerald-500/30" data-testid="alert-time-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-emerald-100/70 text-xs">Descripción</Label>
                    <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Alta volatilidad esperada en USD..." rows={2} className="bg-black/50 border-emerald-500/30" data-testid="alert-desc-input" />
                  </div>
                  <div>
                    <Label className="text-emerald-100/70 text-xs">Impacto</Label>
                    <Select value={form.impact} onValueChange={(v) => setForm({...form, impact: v})}>
                      <SelectTrigger className="bg-black/50 border-emerald-500/30" data-testid="alert-impact-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#050a05] border-emerald-500/30">
                        <SelectItem value="high">Alto</SelectItem>
                        <SelectItem value="medium">Medio</SelectItem>
                        <SelectItem value="low">Bajo</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={createAlert} className="w-full mt-2 bg-emerald-500 text-black hover:bg-emerald-400" data-testid="alert-submit-btn">Crear Alerta</Button>
                  </div>
                </div>
              </div>
            )}

            {/* Alerts List */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="alerts-list">
              {alerts.length === 0 ? (
                <div className="glass-panel rounded-lg p-6 col-span-full text-center text-emerald-100/40">
                  No hay alertas activas
                </div>
              ) : alerts.map((alert) => {
                const timeStatus = getTimeStatus(alert.event_time);
                return (
                  <div key={alert.id} className={`glass-panel rounded-lg p-4 border-l-4 ${impactColors[alert.impact]} ${timeStatus.urgent ? 'animate-pulse' : ''}`} data-testid={`alert-${alert.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          {timeStatus.urgent && <Zap className="h-4 w-4 text-red-400" />}
                          <span className={`text-xs px-2 py-0.5 rounded ${impactColors[alert.impact]}`}>{impactLabels[alert.impact]}</span>
                          <span className="text-emerald-100/50 text-xs flex items-center"><Clock className="h-3 w-3 mr-1" />{timeStatus.text}</span>
                        </div>
                        <p className="text-emerald-50 font-bold text-sm">{alert.title}</p>
                        {alert.description && <p className="text-emerald-100/60 text-xs mt-1">{alert.description}</p>}
                        <p className="text-emerald-100/30 text-xs mt-1">{new Date(alert.event_time).toLocaleString('es-CO', { timeZone: 'America/Bogota', dateStyle: 'short', timeStyle: 'short' })}</p>
                      </div>
                      {user?.role === 'admin' && (
                        <button onClick={() => deleteAlert(alert.id)} className="text-emerald-100/20 hover:text-red-400 ml-2"><Trash2 className="h-3 w-3" /></button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Two column layout: Calendar + Market Overview */}
          <div className="grid lg:grid-cols-5 gap-6">
            {/* Economic Calendar - 3 columns */}
            <div className="lg:col-span-3">
              <div className="flex items-center space-x-2 mb-4">
                <Calendar className="h-5 w-5 text-emerald-500" />
                <h2 className="text-xl font-bold text-emerald-50" style={{ fontFamily: 'Rajdhani, sans-serif' }}>CALENDARIO ECONÓMICO</h2>
              </div>
              <div className="glass-panel rounded-lg overflow-hidden" style={{ height: '600px' }} data-testid="economic-calendar">
                <iframe
                  src="https://sslecal2.investing.com?columns=exc_flags,exc_currency,exc_importance,exc_actual,exc_forecast,exc_previous&features=datepicker,timezone&countries=25,32,6,37,72,22,17,39,14,10,35,43,56,36,110,11,26,12,4,5&calType=week&timeZone=7&lang=4"
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allowTransparency="true"
                  style={{ border: 'none' }}
                  title="Calendario Económico"
                ></iframe>
              </div>
            </div>

            {/* Market Info - 2 columns */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <TrendingUp className="h-5 w-5 text-emerald-500" />
                  <h2 className="text-xl font-bold text-emerald-50" style={{ fontFamily: 'Rajdhani, sans-serif' }}>MERCADOS EN VIVO</h2>
                </div>
                <div className="glass-panel rounded-lg overflow-hidden" style={{ height: '280px' }} data-testid="market-ticker">
                  <iframe
                    src="https://ssltsw.forexprostools.com?lang=4&forex=1,2,3,5,9,10&commodities=8830,8836,8831,8849&indices=166,172,175,179,20273&stocks=&tabs=2,3,1"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    allowTransparency="true"
                    style={{ border: 'none' }}
                    title="Mercados"
                  ></iframe>
                </div>
              </div>

              {/* Quick Tips */}
              <div className="glass-panel rounded-lg p-5" data-testid="trading-tips">
                <h3 className="text-lg font-bold text-emerald-50 mb-3" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                  GUÍA DE IMPACTO
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3 bg-red-500/5 border border-red-500/15 rounded-lg p-3">
                    <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-red-400 font-bold text-sm">Impacto Alto</p>
                      <p className="text-emerald-100/60 text-xs">NFP, Tasas de interés, CPI, discursos de presidentes de bancos centrales. Espera movimientos de 50-200+ pips.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 bg-orange-500/5 border border-orange-500/15 rounded-lg p-3">
                    <AlertTriangle className="h-4 w-4 text-orange-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-orange-400 font-bold text-sm">Impacto Medio</p>
                      <p className="text-emerald-100/60 text-xs">PMI, ventas minoristas, confianza del consumidor. Movimientos de 20-50 pips.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 bg-yellow-500/5 border border-yellow-500/15 rounded-lg p-3">
                    <AlertTriangle className="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-yellow-400 font-bold text-sm">Impacto Bajo</p>
                      <p className="text-emerald-100/60 text-xs">Datos secundarios. Generalmente no afectan significativamente el mercado.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
